import React from 'react';

import { createColumnHelper } from '@tanstack/react-table';
import throttle from 'lodash/throttle';
import { useIntl } from 'react-intl';

import { PERMISSION } from 'src/features/auth';
import { useListMemberships } from 'src/features/membership/api/useListMemberships';
import CellLinkWithIcon from 'src/features/membership/components/cell/CellLinkWIthIcon';
import { membershipMessages } from 'src/features/membership/utils/messages';
import { useListMembershipTypes } from 'src/features/membershipType/api/useListMembershipTypes';
import { membershipTypeMessages } from 'src/features/membershipType/utils/messages';
import { PATH } from 'src/features/navigation/utils/constants';
import { LIST_PAST_STAYS_RESOURCE, useListPastStays } from 'src/features/stay/api/useListPastStays';
import { CellActiveStaysTableActions } from 'src/features/stay/components/cell/CellActiveStaysTableActions';
import PastStaysSidePanel from 'src/features/stay/components/sidePanel/PastStaysSidePanel';
import { PastStay } from 'src/features/stay/types';
import WSContext, { WebSocketGroup } from 'src/features/visitor/context/WSContext';
import { WebSocketEvent } from 'src/features/visitor/types';
import { visitorCategoryBadgeOptions } from 'src/features/visitor/utils/constants';
import { visitorMessages } from 'src/features/visitor/utils/messages';
import { NEW_STAY_AND_PASS_EVENTS } from 'src/features/visitor/utils/wsEvents';
import { useListZones } from 'src/features/zone/api/useListZones';
import { zoneMessages } from 'src/features/zone/utils/messages';
import { useGetStayDuration } from 'src/hooks/useGetStayDuration';
import useHasPermissions from 'src/hooks/useHasPermissions';
import { queryClient } from 'src/lib/react-query';
import { useParking } from 'src/providers/ParkingProvider';
import { LinkWithIcon } from 'src/registry/components/navigation/LinkWithIcon';
import SidePanel from 'src/registry/components/sidePanel/SidePanel';
import {
    CellBadge,
    CellDatetime,
    DataTable,
    DataTableColumnHeader,
    DataTableControls,
    DataTablePagination,
} from 'src/registry/components/table';
import { CellAction } from 'src/registry/components/table/cells/CellAction';
import { INPUT_TYPE } from 'src/registry/components/table/utils/constants';
import useFilters from 'src/registry/hooks/useFilters';
import usePagination from 'src/registry/hooks/usePagination';
import useSearch from 'src/registry/hooks/useSearch';
import { useSingleRowSelection } from 'src/registry/hooks/useSingleRowSelection';
import useSorting from 'src/registry/hooks/useSorting';
import { commonMessages } from 'src/utils/messages';

type Props = {
    membership?: string;
    visitor?: string;
    entityId?: string;
    enableSearchParams?: boolean;
};

const columnHelper = createColumnHelper<PastStay>();
const searchOptions = { globalFilterKey: 'search' };
const sortingOptions = { initialSortingState: [{ id: 'exit_time', desc: true }] };

export const PAST_STAYS_PANEL_NAME = 'past-stays-panel';

const PastStaysTable = ({ visitor, membership, entityId, enableSearchParams = false }: Props) => {
    const { formatMessage, locale } = useIntl();
    const { queryPaginationOptions, ...pagination } = usePagination();
    const { querySortingOptions, ...sorting } = useSorting(sortingOptions);
    const { querySearchOptions, ...search } = useSearch(searchOptions);
    const { queryFiltersOptions, ...filters } = useFilters();

    const accessVisitors = useHasPermissions([PERMISSION.ACCESS_VISITORS]);
    const accessEntitiesPermission = useHasPermissions([PERMISSION.ACCESS_ENTITIES]);

    const { subscribe, unsubscribe } = React.useContext(WSContext);

    React.useEffect(() => {
        const handleWebSocket = throttle((event: WebSocketEvent) => {
            if (NEW_STAY_AND_PASS_EVENTS.includes(event.message)) {
                void queryClient.invalidateQueries({
                    queryKey: [LIST_PAST_STAYS_RESOURCE],
                });
            }
        }, 500);
        subscribe(WebSocketGroup.GATEWAY_PASS, handleWebSocket);

        return () => {
            handleWebSocket.cancel();
            unsubscribe(WebSocketGroup.GATEWAY_PASS, handleWebSocket);
        };
    }, [subscribe, unsubscribe]);

    const [searchMembership, setSearchMembership] = React.useState<string>('');
    const [searchMembershipType, setSearchMembershipType] = React.useState<string>('');

    const { rowSelectionState, clearSelection, setRowSelectionState } = useSingleRowSelection();
    const { getDurationFromStartDateToEndDate } = useGetStayDuration();

    const singleRowSelection = React.useCallback(
        (rowId: string) => {
            setRowSelectionState({ [rowId]: true });
        },
        [setRowSelectionState],
    );

    const [defaultDateFilter] = React.useState(() => {
        const now = Date.now();

        return {
            afterDate: new Date(now - 30 * 24 * 60 * 60 * 1000),
            beforeDate: new Date(),
        };
    });

    const { parkingId } = useParking();

    const { data: listMemberships } = useListMemberships({
        parkingId,
        search: searchMembership,
        config: { enabled: filters.columnFiltersShowDropdownState.includes('membership') },
    });

    const { data: listMembershipTypes } = useListMembershipTypes({
        parkingId,
        search: searchMembershipType,
        config: { enabled: filters.columnFiltersShowDropdownState.includes('membership_type') },
    });

    const {
        data: pastStaysData,
        isError,
        isLoading,
    } = useListPastStays({
        parkingId,
        ...queryPaginationOptions,
        ...querySearchOptions,
        ...querySortingOptions,
        ...queryFiltersOptions,
        ...(visitor ? { visitor } : {}),
        ...(membership ? { membership } : {}),
        ...(entityId ? { visitor_owner: entityId } : {}),
    });

    const { data: zoneData } = useListZones({ parkingId });

    const membershipsFilterOptions = React.useMemo(
        () =>
            listMemberships?.results?.map((membership) => {
                return { value: membership.id, label: membership?.name };
            }),
        [listMemberships?.results],
    );

    const membershipTypesFilterOptions = React.useMemo(
        () =>
            listMembershipTypes?.results?.map((type) => {
                const translatedType = type.translations.find((t) => t.language === locale);

                return { value: type.id, label: translatedType?.name };
            }),
        [listMembershipTypes?.results, locale],
    );

    const zonesFilterOptions = React.useMemo(
        () =>
            zoneData?.results?.map((zone) => {
                const translatedType = zone.translations.find((z) => z.language === locale);

                return { value: zone.id, label: translatedType?.name };
            }),
        [zoneData?.results, locale],
    );

    const columns = [
        columnHelper.accessor('ref', {
            header: DataTableColumnHeader,
            cell: (ctx) => (
                <CellAction {...ctx} sidePanelName={PAST_STAYS_PANEL_NAME} singleRowSelection={singleRowSelection} />
            ),
            enableHiding: true,
            enableSorting: true,
            meta: {
                title: formatMessage(commonMessages.refNumber),
            },
        }),
        ...(!visitor
            ? [
                  columnHelper.accessor('main_identifier.value', {
                      header: DataTableColumnHeader,
                      cell: ({ row }) => {
                          return (
                              <LinkWithIcon
                                  to={`/${PATH.VISITORS}/visitor/${row.original.visitor.id}`}
                                  linkTitle={row.original.main_identifier.value}
                                  hasPermissions={accessVisitors}
                              />
                          );
                      },
                      enableHiding: true,
                      enableSorting: true,
                      meta: {
                          title: formatMessage(visitorMessages.visitor),
                      },
                      id: 'main_identifier',
                  }),
              ]
            : []),
        columnHelper.accessor('additional_identifiers.value', {
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: ({ row }) => {
                const identifiers = row.original.additional_identifiers.map((identifier) => identifier.value);
                const mainIdentifier = row.original.main_identifier.value;
                if (mainIdentifier) identifiers.unshift(mainIdentifier);

                return identifiers.length > 0 ? identifiers.join(', ') : '-';
            },
            meta: {
                title: formatMessage(visitorMessages.identifiers),
            },
        }),
        ...(!entityId
            ? [
                  columnHelper.accessor('visitor_owner.name', {
                      id: 'owner_name',
                      header: DataTableColumnHeader,
                      enableHiding: true,
                      enableSorting: true,
                      cell: ({ row }) => {
                          const entityName = row.original?.visitor_owner?.name;
                          const ownerId = row.original?.visitor_owner?.id;
                          const entityType = row.original?.visitor_owner?.type;

                          return ownerId && entityType ? (
                              <CellLinkWithIcon
                                  toPath={`/${PATH.ENTITIES}/${entityType}/${ownerId}`}
                                  linkTitle={entityName}
                                  hasPermissions={accessEntitiesPermission}
                              />
                          ) : (
                              '-'
                          );
                      },
                      meta: {
                          title: formatMessage(visitorMessages.owner),
                      },
                  }),
              ]
            : []),
        columnHelper.accessor('period_visitor_category', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableColumnFilter: true,
            cell: (props) => <CellBadge badgeOptions={visitorCategoryBadgeOptions} {...props} />,
            meta: {
                title: formatMessage(visitorMessages.type),
                badgeOptions: visitorCategoryBadgeOptions,
                inputType: INPUT_TYPE.MULTI_SELECT,
            },
            id: 'visitor_category',
        }),
        columnHelper.accessor('entry_time', {
            header: DataTableColumnHeader,
            cell: CellDatetime,
            enableHiding: true,
            enableSorting: true,
            enableColumnFilter: true,
            meta: {
                title: formatMessage(visitorMessages.enteredAt),
                inputType: INPUT_TYPE.DATE,
                dateFilter: defaultDateFilter,
                inlineDateFilter: true,
            },
        }),
        columnHelper.accessor('exit_time', {
            header: DataTableColumnHeader,
            cell: CellDatetime,
            enableHiding: true,
            enableSorting: true,
            enableColumnFilter: true,
            meta: {
                title: formatMessage(visitorMessages.exitAt),
                inputType: INPUT_TYPE.DATE,
                dateFilter: defaultDateFilter,
                inlineDateFilter: true,
            },
        }),
        columnHelper.accessor('entry_gateway', {
            header: DataTableColumnHeader,
            cell: ({ row }) => {
                return row.original.entry_gateway?.translations?.find((zone) => zone.language === locale)?.name ?? '-';
            },
            enableHiding: true,
            enableSorting: true,
            meta: {
                title: formatMessage(visitorMessages.entryPoint),
            },
        }),
        columnHelper.accessor('exit_gateway', {
            header: DataTableColumnHeader,
            cell: ({ row }) => {
                return row.original.exit_gateway?.translations?.find((zone) => zone.language === locale)?.name ?? '-';
            },
            enableHiding: true,
            enableSorting: true,
            meta: {
                title: formatMessage(visitorMessages.exitPoint),
            },
        }),
        columnHelper.accessor('duration', {
            header: DataTableColumnHeader,
            cell: ({ row }) => {
                return getDurationFromStartDateToEndDate({
                    startDate: row.original.entry_time,
                    endDate: row.original.exit_time,
                });
            },
            enableHiding: true,
            enableSorting: true,
            meta: {
                title: formatMessage(visitorMessages.duration),
            },
        }),
        columnHelper.display({
            id: 'actions',
            cell: (props) => (
                <CellActiveStaysTableActions
                    {...props}
                    sidePanelName={PAST_STAYS_PANEL_NAME}
                    singleRowSelection={singleRowSelection}
                />
            ),
        }),
        columnHelper.accessor('ref', {
            enableColumnFilter: true,
            meta: {
                title: formatMessage(membershipMessages.memberships),
                options: membershipsFilterOptions,
                inputType: INPUT_TYPE.SEARCH,
            },
            id: 'membership',
        }),
        columnHelper.accessor('ref', {
            enableColumnFilter: true,
            meta: {
                title: formatMessage(membershipTypeMessages.membershipTemplates),
                options: membershipTypesFilterOptions,
                inputType: INPUT_TYPE.SEARCH,
            },
            id: 'membership_type',
        }),
        columnHelper.accessor('ref', {
            enableColumnFilter: true,
            meta: {
                title: formatMessage(zoneMessages.zones),
                options: zonesFilterOptions,
                inputType: INPUT_TYPE.MULTI_SELECT,
            },
            id: 'zones',
        }),
        columnHelper.accessor('ref', {
            header: DataTableColumnHeader,
            cell: CellDatetime,
            enableColumnFilter: true,
            meta: {
                title: formatMessage(visitorMessages.activeAsOf),
                inputType: INPUT_TYPE.DATE,
                dateFilter: defaultDateFilter,
                inlineDateFilter: true,
            },
            id: 'active_as_of',
        }),
    ];

    const selectedStayId = React.useMemo(() => {
        const firstSelected = Object.keys(rowSelectionState)[0];
        if (!pastStaysData || !firstSelected) return;
        return pastStaysData.results.find((row) => row.id === firstSelected)?.id;
    }, [pastStaysData, rowSelectionState]);

    const searchPlaceholder = `${formatMessage(commonMessages.refNumber)}, ${formatMessage(visitorMessages.visitor)}, ${formatMessage(visitorMessages.identifiers)}, ${formatMessage(visitorMessages.owner)}`;

    return (
        <>
            <DataTable<PastStay, any>
                columns={columns}
                data={pastStaysData}
                pagination={pagination}
                sorting={sorting}
                filters={filters}
                search={search}
                semantics={{
                    title: formatMessage(visitorMessages.pastStays),
                    entryName: formatMessage(visitorMessages.stay),
                    entryNames: formatMessage(visitorMessages.stays),
                    placeholder: searchPlaceholder,
                    initialColumnVisibility: {
                        main_identifier: !visitor,
                        membership: false,
                        membership_type: false,
                        active_as_of: false,
                        zones: false,
                    },
                    sidePanelName: PAST_STAYS_PANEL_NAME,
                }}
                isError={isError}
                isLoading={isLoading}
                isMultiSelect
                getRowId={(row) => row.id.toString()}
                components={{
                    controls: DataTableControls,
                    pagination: DataTablePagination,
                }}
                onFilterSearch={(columnId, value) => {
                    if (columnId === 'membership') {
                        setSearchMembership(value);
                    }
                    if (columnId === 'membership_type') {
                        setSearchMembershipType(value);
                    }
                }}
                enableSearchParams={enableSearchParams}
            />
            <SidePanel onClose={clearSelection} name={PAST_STAYS_PANEL_NAME}>
                <PastStaysSidePanel selectedStayId={selectedStayId} />
            </SidePanel>
        </>
    );
};

export default PastStaysTable;
